// =============================================================================
// Admin Role Form Page — Create / Edit admin role with full permission picker
// =============================================================================
// Dual-mode page:
//   /roles/new        → create mode (empty form)
//   /roles/:id/edit   → edit mode (loads existing role + permissions)
// =============================================================================

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Button,
  Badge,
  Input,
  Spinner,
} from '@tkpod/ui';
import { api } from '@/lib/api';
import type {
  AdminRoleDetail,
  CreateAdminRoleRequest,
  UpdateAdminRoleRequest,
  PermissionGroup,
} from '@tkpod/shared';

// ---------------------------------------------------------------------------
// API response types
// ---------------------------------------------------------------------------

interface RoleDetailResponse {
  data: AdminRoleDetail;
}

interface PermissionsResponse {
  groups: PermissionGroup[];
}

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface RoleFormState {
  name: string;
  slug: string;
  description: string;
  permissions: string[];
}

const EMPTY_FORM: RoleFormState = {
  name: '',
  slug: '',
  description: '',
  permissions: [],
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminRoleFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isEditMode = !!id;

  // -- Local state ----------------------------------------------------------
  const [form, setForm] = useState<RoleFormState>(EMPTY_FORM);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // -- Queries --------------------------------------------------------------

  // Load existing role in edit mode
  const roleQuery = useQuery<RoleDetailResponse>({
    queryKey: ['admin-roles', id],
    queryFn: () => api.get<RoleDetailResponse>(`/api/v1/admin/roles/${id}`),
    enabled: isEditMode,
  });

  // Load all available permissions
  const permissionsQuery = useQuery<PermissionsResponse>({
    queryKey: ['admin-roles-permissions'],
    queryFn: () => api.get<PermissionsResponse>('/api/v1/admin/roles/permissions'),
  });

  // Reset form when switching from edit → create mode (same component, different route)
  useEffect(() => {
    if (!isEditMode) {
      setForm(EMPTY_FORM);
    }
  }, [isEditMode]);

  // Populate form when role data loads (edit mode)
  useEffect(() => {
    if (roleQuery.data?.data) {
      const role = roleQuery.data.data;

      // System roles cannot be edited — redirect to detail
      if (role.isSystemRole) {
        void navigate(`/roles/${id}`, { replace: true });
        return;
      }

      setForm({
        name: role.name,
        slug: role.slug,
        description: role.description ?? '',
        permissions: [...role.permissions],
      });
    }
  }, [roleQuery.data, id, navigate]);

  // -- Mutations ------------------------------------------------------------

  const createMutation = useMutation({
    mutationFn: (body: CreateAdminRoleRequest) =>
      api.post<RoleDetailResponse>('/api/v1/admin/roles', body),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      navigate(`/roles/${result.data.id}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (body: UpdateAdminRoleRequest) =>
      api.patch<RoleDetailResponse>(`/api/v1/admin/roles/${id}`, body),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-roles'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-roles', id] }),
      ]);
      navigate(`/roles/${id}`);
    },
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const mutationError =
    createMutation.error?.message ?? updateMutation.error?.message ?? null;

  // -- Form handlers --------------------------------------------------------

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const name = e.target.value;
      setForm((prev) => ({
        ...prev,
        name,
        slug: isEditMode ? prev.slug : slugify(name),
      }));
    },
    [isEditMode],
  );

  const togglePermission = useCallback((key: string) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter((p) => p !== key)
        : [...prev.permissions, key],
    }));
  }, []);

  const toggleGroup = useCallback((group: PermissionGroup) => {
    setForm((prev) => {
      const groupKeys = group.permissions.map((p) => p.key);
      const allSelected = groupKeys.every((k) => prev.permissions.includes(k));
      if (allSelected) {
        return {
          ...prev,
          permissions: prev.permissions.filter((p) => !groupKeys.includes(p)),
        };
      }
      const merged = new Set([...prev.permissions, ...groupKeys]);
      return { ...prev, permissions: [...merged] };
    });
  }, []);

  const toggleGroupCollapse = useCallback((resource: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(resource)) {
        next.delete(resource);
      } else {
        next.add(resource);
      }
      return next;
    });
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      const trimmedName = form.name.trim();
      const trimmedDesc = form.description.trim();

      if (!trimmedName) return;

      if (isEditMode) {
        createMutation.reset();
        updateMutation.mutate({
          name: trimmedName,
          description: trimmedDesc,
          permissions: form.permissions,
        });
      } else {
        updateMutation.reset();
        createMutation.mutate({
          name: trimmedName,
          description: trimmedDesc || undefined,
          permissions: form.permissions,
        });
      }
    },
    [isEditMode, form, createMutation, updateMutation],
  );

  // -- Permission groups with selection counts ------------------------------
  const permissionGroups = useMemo(() => {
    const groups = permissionsQuery.data?.groups ?? [];
    return groups.map((group) => {
      const groupKeys = group.permissions.map((p) => p.key);
      const selectedCount = groupKeys.filter((k) =>
        form.permissions.includes(k),
      ).length;
      return {
        ...group,
        selectedCount,
        allSelected: groupKeys.length > 0 && selectedCount === groupKeys.length,
        someSelected: selectedCount > 0 && selectedCount < groupKeys.length,
      };
    });
  }, [permissionsQuery.data, form.permissions]);

  const totalPermissions = useMemo(
    () =>
      (permissionsQuery.data?.groups ?? []).reduce(
        (sum, g) => sum + g.permissions.length,
        0,
      ),
    [permissionsQuery.data],
  );

  // -- Loading state --------------------------------------------------------

  if (isEditMode && roleQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" label="Loading role..." />
      </div>
    );
  }

  if (isEditMode && roleQuery.isError) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <p className="text-sm text-red-400">
            Failed to load role: {roleQuery.error?.message ?? 'Unknown error'}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void roleQuery.refetch()}
            >
              Retry
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/roles')}
            >
              Back to Roles
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // -- Render ---------------------------------------------------------------

  return (
    <form onSubmit={handleSubmit} className="space-y-6 pb-24">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-text-tertiary">
        <Link
          to="/roles"
          className="hover:text-text-primary transition-colors"
        >
          Roles
        </Link>
        <span>/</span>
        {isEditMode && roleQuery.data?.data && (
          <>
            <Link
              to={`/roles/${id}`}
              className="hover:text-text-primary transition-colors"
            >
              {roleQuery.data.data.name}
            </Link>
            <span>/</span>
          </>
        )}
        <span className="text-text-primary">
          {isEditMode ? 'Edit' : 'Create Role'}
        </span>
      </nav>

      {/* Error banner */}
      {mutationError && (
        <div className="rounded-md bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
          {mutationError}
        </div>
      )}

      {/* Role details card */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold text-text-primary">
            {isEditMode ? 'Edit Role' : 'Create Role'}
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            {isEditMode
              ? 'Update the role name, description, and permissions.'
              : 'Define a new role with a name, slug, and permission set.'}
          </p>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Role Name"
              value={form.name}
              onChange={handleNameChange}
              placeholder="e.g. Content Manager"
              required
              autoFocus
            />
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary">
                Slug
              </label>
              <div className="flex h-[38px] items-center rounded-md border border-border bg-surface-secondary px-3">
                <code className="text-sm text-text-secondary">
                  {form.slug || (
                    <span className="text-text-tertiary">auto-generated</span>
                  )}
                </code>
              </div>
              <p className="mt-1 text-xs text-text-tertiary">
                {isEditMode
                  ? 'Slug cannot be changed after creation.'
                  : 'Auto-generated from name. Used as a unique identifier.'}
              </p>
            </div>
          </div>

          <div className="mt-4">
            <label className="mb-1.5 block text-sm font-medium text-text-primary">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, description: e.target.value }))
              }
              placeholder="Optional description of this role's purpose..."
              rows={2}
              className="block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 resize-y"
            />
          </div>
        </div>
      </Card>

      {/* Permissions card */}
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-text-primary">
                Permissions
              </h2>
              <p className="mt-1 text-sm text-text-secondary">
                Select which permissions this role should have.
              </p>
            </div>
            {form.permissions.length > 0 && (
              <Badge variant="primary" size="sm">
                {form.permissions.length}/{totalPermissions} selected
              </Badge>
            )}
          </div>

          {permissionsQuery.isLoading ? (
            <div className="mt-6 flex items-center justify-center py-12">
              <Spinner size="sm" label="Loading permissions..." />
            </div>
          ) : permissionsQuery.isError ? (
            <div className="mt-6 rounded-md bg-red-500/10 px-4 py-3 text-sm text-red-400">
              Failed to load permissions.
              <Button
                variant="ghost"
                size="sm"
                className="ml-2"
                onClick={() => void permissionsQuery.refetch()}
              >
                Retry
              </Button>
            </div>
          ) : (
            <div className="mt-6 space-y-2">
              {permissionGroups.map((group) => {
                const isCollapsed = collapsedGroups.has(group.resource);

                return (
                  <div
                    key={group.resource}
                    className="rounded-lg border border-border overflow-hidden"
                  >
                    {/* Group header — always visible */}
                    <div className="flex items-center gap-3 bg-surface-secondary px-4 py-3">
                      <input
                        type="checkbox"
                        checked={group.allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = group.someSelected;
                        }}
                        onChange={() => toggleGroup(group)}
                        className="h-4 w-4 rounded border-border text-brand-600 focus:ring-brand-500"
                      />
                      <button
                        type="button"
                        onClick={() => toggleGroupCollapse(group.resource)}
                        className="flex flex-1 items-center gap-2 text-left"
                      >
                        <span className="text-sm font-semibold text-text-primary">
                          {group.label}
                        </span>
                        <Badge variant="default" size="sm">
                          {group.selectedCount}/{group.permissions.length}
                        </Badge>
                        <svg
                          className={`ml-auto h-4 w-4 text-text-tertiary transition-transform ${
                            isCollapsed ? '' : 'rotate-180'
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                    </div>

                    {/* Permission items — collapsible */}
                    {!isCollapsed && (
                      <div className="grid grid-cols-1 gap-1 p-3 sm:grid-cols-2 lg:grid-cols-3">
                        {group.permissions.map((perm) => (
                          <label
                            key={perm.key}
                            className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-surface-secondary transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={form.permissions.includes(perm.key)}
                              onChange={() => togglePermission(perm.key)}
                              className="h-3.5 w-3.5 rounded border-border text-brand-600 focus:ring-brand-500"
                            />
                            <span className="text-sm text-text-secondary">
                              {perm.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {permissionGroups.length === 0 && (
                <p className="py-8 text-center text-sm text-text-tertiary">
                  No permissions available.
                </p>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Sticky bottom bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-surface/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3 sm:px-6">
          <div className={`text-sm ${form.permissions.length === 0 ? 'text-amber-400' : 'text-text-tertiary'}`}>
            {form.permissions.length > 0
              ? `${form.permissions.length} permission${form.permissions.length !== 1 ? 's' : ''} selected`
              : 'No permissions selected — role will have no access'}
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                navigate(isEditMode ? `/roles/${id}` : '/roles')
              }
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!form.name.trim() || isSaving}
              loading={isSaving}
            >
              {isEditMode ? 'Save Changes' : 'Create Role'}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
